import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import {
  Bell, Mail, MessageSquare, Plus, Trash2, Send, Loader2,
  CheckCircle, XCircle, Clock, AlertTriangle, PlayCircle, StopCircle, BarChart3,
  Sparkles
} from 'lucide-react';

const TRIGGER_LABELS = {
  intake_submitted: { label: 'Use Case Submitted', icon: Sparkles, description: 'When a requester submits a new use case' },
  sprint_started: { label: 'Sprint Started', icon: PlayCircle, description: 'When a sprint is activated' },
  sprint_ended: { label: 'Sprint Ended', icon: StopCircle, description: 'When a sprint is completed' },
  weekly_report: { label: 'Weekly Report', icon: BarChart3, description: 'Scheduled weekly progress summary' },
  blocker_alert: { label: 'Blocker Alert', icon: AlertTriangle, description: 'When a story is blocked' },
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function NotificationSettings() {
  const can = usePermissions();
  const canManage = can('manageNotifications');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const [settings, setSettings] = useState({
    teams_webhook_url: '',
    email_recipients: [],
    triggers: {
      intake_submitted: { enabled: true, channels: ['teams'] },
      sprint_started: { enabled: true, channels: ['teams', 'email'] },
      sprint_ended: { enabled: true, channels: ['teams', 'email'] },
      weekly_report: { enabled: true, channels: ['teams', 'email'], day: 'monday', hour: 9 },
      blocker_alert: { enabled: true, channels: ['teams'] },
    },
  });

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/notifications/settings');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/notifications/history', { params: { limit: 50 } });
      setHistory(res.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadHistory();
  }, [loadSettings, loadHistory]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', settings);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const testNotification = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/notifications/test');
      setTestResult(res.data.results);
      loadHistory();
    } catch (err) {
      setTestResult({ error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const addEmail = () => {
    const email = newEmail.trim();
    if (email && email.includes('@') && !settings.email_recipients.includes(email)) {
      setSettings(prev => ({
        ...prev,
        email_recipients: [...prev.email_recipients, email],
      }));
      setNewEmail('');
    }
  };

  const removeEmail = (email) => {
    setSettings(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email),
    }));
  };

  const toggleTrigger = (triggerKey) => {
    setSettings(prev => ({
      ...prev,
      triggers: {
        ...prev.triggers,
        [triggerKey]: {
          ...prev.triggers[triggerKey],
          enabled: !prev.triggers[triggerKey]?.enabled,
        },
      },
    }));
  };

  const toggleChannel = (triggerKey, channel) => {
    setSettings(prev => {
      const current = prev.triggers[triggerKey]?.channels || [];
      const updated = current.includes(channel)
        ? current.filter(c => c !== channel)
        : [...current, channel];
      return {
        ...prev,
        triggers: {
          ...prev.triggers,
          [triggerKey]: { ...prev.triggers[triggerKey], channels: updated },
        },
      };
    });
  };

  const updateWeeklySchedule = (field, value) => {
    setSettings(prev => ({
      ...prev,
      triggers: {
        ...prev.triggers,
        weekly_report: { ...prev.triggers.weekly_report, [field]: value },
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#98139C]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#30302F] dark:text-white flex items-center gap-2">
            <Bell size={24} /> Notification Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configure automatic Teams and email notifications for sprint events.
          </p>
        </div>
        {canManage ? (
        <button
          onClick={saveSettings}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-10 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Save Settings
        </button>
        ) : (
          <span className="text-xs text-[#30302F]/70 dark:text-white/70">Read only. Managers change notification settings.</span>
        )}
      </div>

      <fieldset disabled={!canManage} className="contents">
      {/* Teams Integration */}
      <section className="mb-8 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#30302F] dark:text-white flex items-center gap-2 mb-4">
          <MessageSquare size={20} /> Microsoft Teams
        </h2>
        <div className="space-y-3">
          <label className="block text-sm font-medium text-[#30302F] dark:text-gray-300">
            Incoming Webhook URL
          </label>
          <input
            type="url"
            value={settings.teams_webhook_url || ''}
            onChange={e => setSettings(prev => ({ ...prev, teams_webhook_url: e.target.value }))}
            placeholder={!canManage && settings.teams_webhook_configured ? 'Configured (visible to managers)' : 'https://outlook.office.com/webhook/...'}
            className="w-full px-3 py-2 border border-[#e3e3e3] dark:border-white/10 rounded-lg bg-white dark:bg-[#0f0f23] text-[#30302F] dark:text-white text-sm focus:ring-1 focus:ring-[#98139C] focus:border-[#98139C] outline-none"
          />
          <p className="text-xs text-gray-400">
            Create an incoming webhook in your Teams channel settings.
            <a href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook" target="_blank" rel="noopener noreferrer" className="text-[#98139C] ml-1 underline">
              Learn how
            </a>
          </p>
        </div>
      </section>

      {/* Email Recipients */}
      <section className="mb-8 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#30302F] dark:text-white flex items-center gap-2 mb-4">
          <Mail size={20} /> Email Recipients
        </h2>
        <div className="flex gap-2 mb-4">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            placeholder="email@company.com"
            className="flex-1 px-3 py-2 border border-[#e3e3e3] dark:border-white/10 rounded-lg bg-white dark:bg-[#0f0f23] text-[#30302F] dark:text-white text-sm focus:ring-1 focus:ring-[#98139C] focus:border-[#98139C] outline-none"
          />
          <button
            onClick={addEmail}
            className="inline-flex items-center gap-1.5 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-10 transition-colors"
          >
            <Plus size={14} /> Add
          </button>
        </div>
        {settings.email_recipients.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No email recipients configured.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {settings.email_recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] text-sm font-medium">
                {email}
                <button onClick={() => removeEmail(email)} className="hover:text-red-600 dark:hover:text-red-400">
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Triggers */}
      <section className="mb-8 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#30302F] dark:text-white flex items-center gap-2 mb-4">
          <Bell size={20} /> Notification Triggers
        </h2>
        <div className="space-y-4">
          {Object.entries(TRIGGER_LABELS).map(([key, { label, icon: Icon, description }]) => {
            const trigger = settings.triggers[key] || {};
            return (
              <div key={key} className="border border-[#e3e3e3] dark:border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Icon size={18} className={trigger.enabled ? 'text-[#98139C]' : 'text-gray-400'} />
                    <div>
                      <div className="text-sm font-medium text-[#30302F] dark:text-white">{label}</div>
                      <div className="text-xs text-gray-400">{description}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleTrigger(key)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${trigger.enabled ? 'bg-[#98139C]' : 'bg-gray-300 dark:bg-gray-600'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${trigger.enabled ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                {trigger.enabled && (
                  <div className="ml-8 mt-3 flex items-center gap-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Channels:</span>
                    <label className="flex items-center gap-1.5 text-sm text-[#30302F] dark:text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(trigger.channels || []).includes('teams')}
                        onChange={() => toggleChannel(key, 'teams')}
                        className="accent-[#98139C]"
                      />
                      Teams
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-[#30302F] dark:text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(trigger.channels || []).includes('email')}
                        onChange={() => toggleChannel(key, 'email')}
                        className="accent-[#98139C]"
                      />
                      Email
                    </label>
                  </div>
                )}
                {key === 'weekly_report' && trigger.enabled && (
                  <div className="ml-8 mt-3 flex items-center gap-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Schedule:</span>
                    <select
                      value={trigger.day || 'monday'}
                      onChange={e => updateWeeklySchedule('day', e.target.value)}
                      className="px-2 py-1 text-sm border border-[#e3e3e3] dark:border-white/10 rounded-lg bg-white dark:bg-[#0f0f23] text-[#30302F] dark:text-white"
                    >
                      {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                    </select>
                    <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                    <select
                      value={trigger.hour ?? 9}
                      onChange={e => updateWeeklySchedule('hour', parseInt(e.target.value))}
                      className="px-2 py-1 text-sm border border-[#e3e3e3] dark:border-white/10 rounded-lg bg-white dark:bg-[#0f0f23] text-[#30302F] dark:text-white"
                    >
                      {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                    </select>
                    <span className="text-xs text-gray-500 dark:text-gray-400">UTC</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Test Notification */}
      </fieldset>

      {canManage && (
      <section className="mb-8 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#30302F] dark:text-white flex items-center gap-2 mb-4">
          <Send size={20} /> Test Notification
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Send a test message to verify your Teams webhook and email configuration. Save settings first.
        </p>
        <button
          onClick={testNotification}
          disabled={testing}
          className="inline-flex items-center gap-2 border border-[#98139C] text-[#98139C] hover:bg-[#98139C]/5 rounded-full font-bold text-sm px-5 h-10 transition-colors disabled:opacity-50"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send Test
        </button>
        {testResult && (
          <div className="mt-4 p-3 border border-[#e3e3e3] dark:border-white/10 rounded-lg text-sm">
            {testResult.error ? (
              <span className="text-red-600 flex items-center gap-1"><XCircle size={14} /> Error: {testResult.error}</span>
            ) : (
              <div className="space-y-1">
                {testResult.teams !== undefined && (
                  <div className="flex items-center gap-1">
                    {testResult.teams ? <CheckCircle size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                    <span className={testResult.teams ? 'text-green-700 dark:text-green-400' : 'text-red-600'}>
                      Teams: {testResult.teams ? 'Sent' : 'Failed'}
                    </span>
                  </div>
                )}
                {testResult.email !== undefined && (
                  <div className="flex items-center gap-1">
                    {testResult.email ? <CheckCircle size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                    <span className={testResult.email ? 'text-green-700 dark:text-green-400' : 'text-red-600'}>
                      Email: {testResult.email ? 'Sent' : 'Failed'}
                    </span>
                  </div>
                )}
                {Object.keys(testResult).length === 0 && (
                  <span className="text-yellow-600">No channels configured. Add a webhook URL or email recipients first.</span>
                )}
              </div>
            )}
          </div>
        )}
      </section>
      )}

      {/* Notification History */}
      <section className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#30302F] dark:text-white flex items-center gap-2 mb-4">
          <Clock size={20} /> Notification History
        </h2>
        {historyLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-gray-400" size={24} />
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4">No notifications sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e3e3e3] dark:border-white/10">
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Channels</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#e3e3e3] dark:border-white/10 last:border-0">
                    <td className="py-2 px-2 text-[#30302F] dark:text-white whitespace-nowrap">
                      {item.sent_at ? new Date(item.sent_at).toLocaleString() : '-'}
                    </td>
                    <td className="py-2 px-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] text-xs font-medium">
                        {(item.event_type || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-[#30302F] dark:text-white">
                      {(item.channels || []).join(', ')}
                    </td>
                    <td className="py-2 px-2">
                      {item.results && Object.values(item.results).some(v => v) ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Sent</span>
                      ) : (
                        <span className="text-red-500 flex items-center gap-1"><XCircle size={12} /> Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
